import { Subscription } from '@localstack/types';

import { Stack, InputAdornment, TextField, MenuItem, Skeleton } from '@mui/material';
import { ReactElement, useEffect, useMemo } from 'react';
import { Search as SearchIcon } from '@mui/icons-material';

import { debounce } from 'lodash';

import { PageGutter } from '~/layouts';

import { LicenseAssignmentPlanLabel } from './LicenseAssignmentPlanLabel';

import {
  SubscriptionSeatsSummaryDropdown,
  SubscriptionSeatsSummaryDropdownProps,
} from './SubscriptionSeatsSummaryDropdown';

import { StyledSearchField } from './style';

import { Filter } from './types';

type MembersToolbarType = SubscriptionSeatsSummaryDropdownProps & {
  onSearchChange: (query: string) => void;
  searchQueryFilter: Filter;
  onFilterChange: (filter: Filter) => void;
  subscriptionFilter: string;
  onSubscriptionFilterChange: (subscriptionId: string) => void;
  activeSubscriptions: Subscription[];
  isLoading: boolean;
  leftSpace: boolean;
  children: ReactElement;
};
export const MembersToolbar = ({
  onSearchChange,
  searchQueryFilter,
  onFilterChange,
  subscriptionFilter,
  onSubscriptionFilterChange,
  activeSubscriptions,
  isLoading,
  subscriptionsByEmulator,
  subscriptionsSeatsAvailability,
  leftSpace,
  children,
}: MembersToolbarType): ReactElement => {
  const debouncedSetSearchQuery = useMemo(() => debounce(onSearchChange, 500), []);
  useEffect(() => () => debouncedSetSearchQuery.cancel(), [debouncedSetSearchQuery]);

  return (
    <PageGutter variant={leftSpace ? 'full' : undefined}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent={{ xs: 'flex-start', sm: 'space-between' }}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        spacing={{ xs: 2, sm: 0 }}
        sx={{ margin: (theme) => theme.spacing(2, 0) }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={{ xs: 2, sm: 1 }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
        >
          <StyledSearchField
            variant="outlined"
            size="small"
            placeholder="Search members by name or email"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
            onChange={(e) => debouncedSetSearchQuery(e.target.value as string)}
          />
          <TextField
            select
            value={searchQueryFilter}
            size="small"
            variant="outlined"
            onChange={(event) => onFilterChange(event.target.value as Filter)}
          >
            <MenuItem value={Filter.ALL}>All</MenuItem>
            <MenuItem value={Filter.ACTIVE_ALL}>Active</MenuItem>
            <MenuItem value={Filter.ACTIVE_ASSIGNED}>Active (Assigned)</MenuItem>
            <MenuItem value={Filter.ACTIVE_UNASSIGNED}>Active (Unassigned)</MenuItem>
            <MenuItem value={Filter.INVITED}>Invited</MenuItem>
          </TextField>
          {(searchQueryFilter === Filter.ACTIVE_ASSIGNED || searchQueryFilter === Filter.ACTIVE_UNASSIGNED) && (
            <TextField
              select
              value={subscriptionFilter}
              size="small"
              variant="outlined"
              onChange={(event) => onSubscriptionFilterChange(event.target.value as string)}
              SelectProps={{ displayEmpty: true }}
            >
              <MenuItem value="">All subscriptions</MenuItem>
              {Object.values(activeSubscriptions).map((subscription) => (
                <MenuItem key={subscription.id} value={subscription.id}>
                  <LicenseAssignmentPlanLabel emulator={subscription.plan.emulator} family={subscription.plan.family} />
                </MenuItem>
              ))}
            </TextField>
          )}
        </Stack>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={{ xs: 2, sm: 3 }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
        >
          {isLoading ? (
            <Skeleton width={100} />
          ) : (
            <Stack direction="row" alignItems="center" spacing={1}>
              <SubscriptionSeatsSummaryDropdown
                subscriptionsByEmulator={subscriptionsByEmulator}
                subscriptionsSeatsAvailability={subscriptionsSeatsAvailability}
              />
            </Stack>
          )}
          {children}
        </Stack>
      </Stack>
    </PageGutter>
  );
};
